import React, { useState } from "react";
import { Modal, Image, Typography, Tag, Divider } from "antd";
import {
  LinkOutlined,
  LeftCircleFilled,
  RightCircleFilled,
} from "@ant-design/icons";
import Masonry from "react-masonry-css";
import ProjectItem from "./ProjectItem"; // Import the new ProjectItem component
import "./Projects.css";
import Sharedom1 from "../images/Sharedom-1.png";
import Sharedom2 from "../images/Sharedom-2.png";
import Sharedom3 from "../images/Sharedom-3.png";
import SneakIn1 from "../images/SneakIn-1.png";
import SneakIn2 from "../images/SneakIn-2.png";
import SneakIn3 from "../images/SneakIn-3.png";
import YBB1 from "../images/YBB-1.png";
import YBB2 from "../images/YBB-2.png";
import PrivyProbe1 from "../images/PrivyProbe-1.png";
import PrivyProbe2 from "../images/PrivyProbe-2.png";
import PrivyProbe3 from "../images/PrivyProbe-3.png";
import Avatar1 from "../images/project-avatar-1.png";
import Avatar2 from "../images/project-avatar-2.png";
import Avatar3 from "../images/project-avatar-3.png";
import placeholder from "../images/Placeholder.jpg";
import { motion } from "framer-motion";
import { InView } from "react-intersection-observer";

const { Title, Paragraph } = Typography;
const ProjectModal = ({ project, visible, onClose }) => {
  const [currentSlide, setCurrentSlide] = useState(0);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % project.images.length);
  };

  const prevSlide = () => {
    setCurrentSlide(
      (prev) => (prev - 1 + project.images.length) % project.images.length
    );
  };

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={600}
      bodyStyle={{ padding: 0 }}
    >
      <div className="project-modal-content">
        <div className="project-image-carousel">
          <Image
            src={project.images[currentSlide]}
            alt={`${project.title} - Image ${currentSlide + 1}`}
            preview={false}
          />
          <LeftCircleFilled
            className="carousel-arrow left"
            onClick={prevSlide}
          />
          <RightCircleFilled
            className="carousel-arrow right"
            onClick={nextSlide}
          />
          <div className="carousel-indicator">{`${currentSlide + 1} / ${
            project.images.length
          }`}</div>
        </div>
        <div className="project-details">
          <div className="project-header">
            <Title level={3}>{project.title}</Title>
          </div>
          <Divider style={{ margin: "10px 0" }} />
          <Paragraph>{project.description}</Paragraph>
          <div className="project-skills">
            {project.skills.map((skill, index) => (
              <Tag key={index} className="skill-tag">
                #{skill}
              </Tag>
            ))}
          </div>
          {project.link && (
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="project-link"
            >
              <LinkOutlined /> View Project
            </a>
          )}
          <div className="project-date">{project.date}</div>
        </div>
      </div>
    </Modal>
  );
};

function Projects() {
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);

  const avatars = [Avatar1, Avatar2, Avatar3];
  const usernames = ["@boba_addict", "@nap_and_yap", "@orange_cat_energy"];

  const projects = [
    {
      title: "SneakIn",
      blurb: "How much would these shoes cost next week?",
      images: [SneakIn1, SneakIn2, SneakIn3],
      description:
        "Limited edition and luxury sneaker price and demand fluctuates so greatly that the sneaker resale industry is often considered a stock market. It is extremely difficult to predict. SneakIn is an interactive webapp where users can input sneaker details and predict the shoe's current and future sales prices to help make informed decisions for favorable purchases and profitable transactions.",
      skills: ["BERT", "NLP", "NeuralNetwork", "React"],
      link: "https://sharedom.onrender.com/",
      date: "Completed May 2024",
    },
    {
      title: "YouthBuild Boston Interactive Map",
      blurb:
        "Boston community projects and neighborhood statistics at your fingertips.",
      images: [YBB1, YBB2],
      description:
        "An interactive map made with JavaScript and various Google APIs that dynamically displays YouthBuild Boston’s community projects in Boston as well as neighborhood statistics. Our project aims to serve thousands of youth in Boston who receive aid from the nonprofit. ",
      skills: ["GoogleAPIs", "React"],
      link: "https://youthbuild-boston-mapping-project.onrender.com/",
      date: "Completed Jun 2024",
    },
    {
      title: "PrivyProbe",
      blurb: "Ever wondered who's stalking you 🤔",
      images: [PrivyProbe1, PrivyProbe2, PrivyProbe3],
      description:
        "Privacy and personal data tracking has been a rising issue especially the given the rapid advancement of technology tools. The Electronic Frontier Foundation (EFF) advocates for user privacy and free expression, notably through tools like their browser Privacy Badger. PrivyProbe creates a data analysis and visualization dashboard for Privacy Badger tracker data, facilitating improvements, insights, and increased public interest in privacy protection.",
      skills: ["React", "Express.js", "Node.js", "NLP"],
      link: "https://eff-hackathon.onrender.com/",
      date: "Completed Apr 2024",
    },
    {
      title: "Sharedom",
      blurb: "A platform for sharing and finding items you need.",
      images: [Sharedom1, Sharedom2, Sharedom3],
      description: "Description of Sharedom project",
      skills: ["React", "Next.js", "MongoDB", "GoogleAuth"],
      link: "https://sharedom.onrender.com/",
      date: "Completed Jan 2024",
    },
  ];

  const projectsWithAuthors = projects.map((project, index) => ({
    ...project,
    author: usernames[index % usernames.length],
    authorAvatar: avatars[index % avatars.length],
  }));

  const openModal = (project) => {
    setSelectedProject(project);
    setModalVisible(true);
  };

  const breakpointColumnsObj = {
    default: 3,
    1100: 2,
    700: 1,
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="projects-container">
      <Masonry
        breakpointCols={breakpointColumnsObj}
        className="projects-grid"
        columnClassName="projects-grid_column"
      >
        {projectsWithAuthors.map((project, index) => (
          <InView key={index} threshold={0.2} triggerOnce>
            {({ inView, ref }) => (
              <motion.div
                ref={ref}
                variants={itemVariants}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <ProjectItem project={project} onClick={openModal} />
              </motion.div>
            )}
          </InView>
        ))}
      </Masonry>
      {selectedProject && (
        <ProjectModal
          project={selectedProject}
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
        />
      )}
    </div>
  );
}
export default Projects;
